// JOBE-0163.5 — ScorecardFraming: Board Scorecard framing for W4 DepartmentPage.
// Dense category-grouped table for executive/board-level review.
// No sparklines — densest framing by design (screen-guide §W4 "board scorecard is the densest").

import React from 'react';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import './ScorecardFraming.css';

function formatValue(val, unit) {
  if (val == null) return '—';
  return unit ? `${val} ${unit}` : String(val);
}

function formatVariance(variance, unit, status) {
  if (variance == null) return { text: '—', colorClass: 'variance-neutral' };
  const sign      = variance >= 0 ? '+' : '−';
  const abs       = Math.abs(variance);
  const text      = `${sign}${abs}${unit ? ' ' + unit : ''}`;
  const colorClass =
    status === 'ok'   ? 'variance-ok' :
    status === 'bad'  ? 'variance-bad' :
    status === 'warn' ? 'variance-warn' :
                        'variance-neutral';
  return { text, colorClass };
}

// Map scorecard metric status to StatusPill tone + label.
const STATUS_PILL = {
  ok:   { tone: 'ok',   label: 'On track' },
  warn: { tone: 'warn', label: 'Watch' },
  bad:  { tone: 'bad',  label: 'Behind' },
  todo: { tone: 'pencil', label: 'Pending' },
};

export default function ScorecardFraming({ dept }) {
  const { categories } = dept?.scorecard ?? { categories: [] };

  if (!categories || !categories.length) {
    return <p className="scorecard-empty">No scorecard data for this department.</p>;
  }

  return (
    <div className="scorecard-framing" data-testid="framing-scorecard">
      <table className="scorecard-table">
        <thead>
          <tr>
            <th>Metric</th>
            <th>Current</th>
            <th>Target</th>
            <th>Variance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat, ci) => (
            <React.Fragment key={`cat-${ci}`}>
              <tr className="scorecard-category-header">
                <td colSpan={5}>{cat.name.toUpperCase()}</td>
              </tr>
              {cat.metrics.map((metric, mi) => {
                const { text: varText, colorClass } = formatVariance(
                  metric.variance, metric.unit, metric.status
                );
                const pillDef = STATUS_PILL[metric.status];
                return (
                  <tr key={`metric-${ci}-${mi}`} className="scorecard-metric-row">
                    <td className="metric-name">{metric.name}</td>
                    <td className="metric-current mono">
                      {formatValue(metric.current, metric.unit)}
                    </td>
                    <td className="metric-target mono">
                      {formatValue(metric.target, metric.unit)}
                    </td>
                    <td className={`metric-variance mono ${colorClass}`}>
                      {varText}
                    </td>
                    <td className="metric-status">
                      {metric.status === 'neutral'
                        ? <span className="status-absent">—</span>
                        : pillDef
                          ? <StatusPill tone={pillDef.tone} label={pillDef.label} />
                          : <span className="status-absent">—</span>}
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
